import React from "react";
import "../CustomTheme/customTheme.css";
import "./CustomCountChip.css";

const TONES = ["primary", "success", "danger", "warning", "info", "neutral"];

/**
 * CustomCountChip — a highlighted number that opens something (a user list, the next page, an API call).
 * Every clickable count in a table uses it, so they all look and behave the same.
 *
 *   <CustomCountChip value={12} tooltip="View paid users" onClick={openPaid} />
 *
 * Props:
 *  - value      the number (0 / empty → a plain, non-clickable grey chip)
 *  - onClick    what the chip opens; without it the chip is display-only
 *  - tooltip    hover text saying what clicking does (also the screen-reader label)
 *  - tone       "primary" (default) | "success" | "danger" | "warning" | "info" | "neutral"
 *  - format     (value) => text; default Indian digit grouping
 *  - disabled, className
 */
const CustomCountChip = ({ value, onClick, tooltip, tone = "primary", format, disabled = false, className }) => {
  const num = Number(value);
  const isEmpty = value === null || value === undefined || value === "" || (Number.isFinite(num) && num === 0);
  const text = format ? format(value) : Number.isFinite(num) ? num.toLocaleString("en-IN") : String(value ?? "—");
  const safeTone = TONES.includes(tone) ? tone : "primary";

  if (isEmpty || !onClick || disabled) {
    return (
      <span className={`ccc-chip ccc-static${isEmpty ? " ccc-empty" : ` ccc-${safeTone}`}${className ? ` ${className}` : ""}`} title={isEmpty ? undefined : tooltip}>
        {isEmpty ? (value === 0 || value === "0" ? 0 : "—") : text}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`ccc-chip ccc-${safeTone}${className ? ` ${className}` : ""}`}
      onClick={(e) => {
        e.stopPropagation(); // never also trigger a row click
        onClick(e);
      }}
      title={tooltip}
      aria-label={tooltip ? `${text} — ${tooltip}` : undefined}
    >
      {text}
    </button>
  );
};

export default CustomCountChip;
